/**
 * useChoreTemplates — admin chore-template data layer (Fase 3 frontend).
 *
 * Wraps the requireAdminScope-gated template endpoints on /api/chores:
 *   - useChoreTemplates(mode)      GET    /api/chores/templates?mode=
 *   - useCreateChoreTemplate()     POST   /api/chores
 *   - useUpdateChoreTemplate()     PATCH  /api/chores/:id
 *   - useArchiveChoreTemplate()    DELETE /api/chores/:id
 *   - useUnarchiveChoreTemplate()  POST   /api/chores/:id/unarchive
 *   - useDuplicateChoreTemplate()  POST   /api/chores/:id/duplicate
 *
 * Mirrors useMembers.ts: the list query is disabled for non-admin sessions and
 * mutations throw the shared `ApiError` so the admin re-auth modal can detect a
 * 403 admin-scope error (isAdminScopeError) and retry after a fresh re-auth.
 *
 * The template list endpoint returns raw `chores` rows (status='active'). The
 * `recurrence` column is a JSON string — consumers parse it where needed
 * (ChoreTemplateForm does so when prefilling edit mode). Assignees are NOT
 * echoed by the list route, so the form treats assignee editing as opt-in on
 * edit (mirrors MemberForm's "Endre PIN" pattern) to avoid clobbering the set.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './useMembers';
import { useSession } from '../auth/useSession';

/** A `chores` row as returned by GET /api/chores/templates. */
export interface ChoreTemplateRow {
  id: string;
  title: string;
  description: string | null;
  icon_key: string;
  tint: string | null;
  point_value: number;
  min_age: number;
  /** JSON string validated against the shared Recurrence Zod on the server. */
  recurrence: string;
  expires_after_h: number | null;
  proof_required: 'none' | 'optional' | 'required';
  completion_mode: 'shared' | 'each';
  category: string | null;
  status: 'active' | 'draft_proposed' | 'rejected';
  proposed_by: string | null;
  rejected_reason: string | null;
  /**
   * Fase 4: 'homework' templates are one-off lekser surfaced on /lekser.
   * Optional so pre-Fase-4 fixtures still typecheck; the live list route always
   * populates them (the form defaults a missing kind to 'chore').
   */
  kind?: 'chore' | 'homework';
  subject?: string | null;
  due_date?: string | null;
  /**
   * Assignee member ids, resolved server-side by listTemplatesDetailed. Only
   * the ?mode= listings populate this; the legacy shape omits it, hence
   * optional. Empty array = open template (anyone may claim).
   */
  assignee_ids?: string[];
  active: number;
  created_by: string;
  created_at: string;
}

/** Body for POST /api/chores — matches the server's CreateTemplateBody Zod. */
export interface CreateChoreTemplateInput {
  title: string;
  description?: string;
  icon_key: string;
  tint?: string;
  point_value: number;
  min_age?: number;
  recurrence: unknown;
  expires_after_h?: number;
  proof_required?: 'none' | 'optional' | 'required';
  category?: string;
  completion_mode?: 'shared' | 'each';
  assignee_ids?: string[];
  /** Fase 4. kind='homework' forces recurrence {type:'once'} (server-enforced). */
  kind?: 'chore' | 'homework';
  subject?: string;
  due_date?: string;
}

/** Body for PATCH /api/chores/:id — every field optional (partial update). */
export type UpdateChoreTemplateInput = Partial<CreateChoreTemplateInput>;

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'unknown';
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === 'string') code = body.error;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, code);
}

export const CHORE_TEMPLATES_KEY = ['admin', 'chore-templates'] as const;

/** Which slice of the library to list. See listTemplates in chores.service. */
export type TemplateMode = 'live' | 'archived';

/**
 * The template library. `mode` is part of the query key so the live and
 * archived lists cache independently — a mutation invalidates the shared
 * CHORE_TEMPLATES_KEY prefix, which refreshes both.
 */
export function useChoreTemplates(mode: TemplateMode = 'live') {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  return useQuery<ChoreTemplateRow[]>({
    queryKey: [...CHORE_TEMPLATES_KEY, mode],
    enabled: isAdmin,
    queryFn: async () => {
      const res = await fetch(`/api/chores/templates?mode=${mode}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as ChoreTemplateRow[];
    },
  });
}

export function useCreateChoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateChoreTemplateInput) => {
      const res = await fetch('/api/chores', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as ChoreTemplateRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHORE_TEMPLATES_KEY });
    },
  });
}

export function useUpdateChoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: UpdateChoreTemplateInput }) => {
      const res = await fetch(`/api/chores/${args.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args.input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as ChoreTemplateRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHORE_TEMPLATES_KEY });
    },
  });
}

export function useArchiveChoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/chores/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as { ok: true };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHORE_TEMPLATES_KEY });
    },
  });
}

/**
 * Restore an archived template. Without this an archived template could only
 * be retyped from scratch — DELETE set active=0 and nothing flipped it back.
 */
export function useUnarchiveChoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/chores/${id}/unarchive`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as { ok: true };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHORE_TEMPLATES_KEY });
    },
  });
}

/**
 * Copy a template, assignees included. Returns the new row so the caller can
 * drop straight into editing it — the point of duplicating is to change
 * something, so landing on the list would just cost another click.
 */
export function useDuplicateChoreTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/chores/${id}/duplicate`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as ChoreTemplateRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHORE_TEMPLATES_KEY });
    },
  });
}
